/**
 * DateField — day/month/year selection in the CLINIC'S calendar system
 * (Jalali or Gregorian), never free text (frozen 3C rule). Emits ISO
 * 'YYYY-MM-DD'. One clinic-level calendar setting, reused — no per-patient
 * preference exists.
 */

import { useId, useMemo, useState } from 'react';
import k from './ui.module.css';
import type { CalendarSystem } from '@/core/clinic/api';
import {
  currentYear,
  daysInMonth,
  isoToParts,
  monthNames,
  partsToIso,
} from '@/core/calendar/dateValue';
import type { Locale } from '@/core/i18n/LocaleProvider';

export function DateField(props: {
  label: string;
  value: string; // ISO 'YYYY-MM-DD' or ''
  onChange: (iso: string) => void;
  system: CalendarSystem;
  locale: Locale;
  help?: string;
  requirement?: { word: string; kind: 'required' | 'optional' };
  /** Years back from the current year offered in the year list. */
  yearsBack?: number;
  /** Years ahead of the current year offered (future-facing fields, e.g. time off). */
  yearsAhead?: number;
}) {
  const id = useId();
  const parts = props.value ? isoToParts(props.value, props.system) : null;
  const [draft, setDraft] = useState<{ year: string; month: string; day: string }>({
    year: parts ? String(parts.year) : '',
    month: parts ? String(parts.month) : '',
    day: parts ? String(parts.day) : '',
  });

  // ZOY-338: a parent that receives its value AFTER mount (Reschedule seeds
  // from the fetched booking; New booking applies a suggestion) must show it.
  // A non-empty valid value that differs from the mounted one resets the
  // selects; the parent's '' during the user's own partial edit never
  // clobbers what they are mid-way through picking.
  const [lastValue, setLastValue] = useState(props.value);
  if (props.value !== lastValue) {
    setLastValue(props.value);
    const next = props.value ? isoToParts(props.value, props.system) : null;
    if (next) {
      setDraft({ year: String(next.year), month: String(next.month), day: String(next.day) });
    }
  }

  const nowYear = currentYear(props.system);
  const years = useMemo(() => {
    const ahead = props.yearsAhead ?? 0;
    const list = Array.from(
      { length: ahead + (props.yearsBack ?? 120) },
      (_, i) => nowYear + ahead - i,
    );
    // A picker must always be able to DISPLAY the value it holds. A date
    // seeded from outside the offered range (a calendar slot in another year,
    // a stored date of birth) would otherwise render as a blank year while the
    // field still committed that year — the same UI/value disagreement
    // ZOY-422 closed. The held year joins the list, in order.
    const held = Number(draft.year);
    if (draft.year !== '' && Number.isFinite(held) && !list.includes(held)) {
      list.push(held);
      list.sort((x, y) => y - x);
    }
    return list;
  }, [nowYear, props.yearsBack, props.yearsAhead, draft.year]);
  const months = useMemo(
    () => monthNames(props.system, props.locale),
    [props.system, props.locale],
  );
  const dayCount =
    draft.year && draft.month
      ? daysInMonth(Number(draft.year), Number(draft.month), props.system)
      : 31;

  const numFmt = new Intl.NumberFormat(props.locale === 'fa' ? 'fa-IR' : 'en-US', {
    useGrouping: false,
  });

  const update = (next: { year: string; month: string; day: string }) => {
    if (next.year && next.month && next.day) {
      const iso = partsToIso(
        { year: Number(next.year), month: Number(next.month), day: Number(next.day) },
        props.system,
      );
      if (iso) {
        setDraft(next);
        props.onChange(iso);
        return;
      }
      // ZOY-422: the picked day does not exist in the new month/year. The
      // picker's convention is CONSTRAINT, not clamping — so the day clears
      // and the parent value clears with it. The UI and the emitted value
      // always agree; the previous date is never silently kept.
      setDraft({ ...next, day: '' });
      props.onChange('');
      return;
    }
    setDraft(next);
    props.onChange('');
  };

  const selectStyle: React.CSSProperties = {
    minBlockSize: 44,
    paddingInline: 10,
    background: 'var(--zc-card)',
    border: '1.5px solid var(--zc-line-strong)',
    borderRadius: 12,
    fontSize: 14,
    color: 'var(--zc-ink)',
    flex: 1,
    minInlineSize: 0,
  };

  return (
    <div className={k.field}>
      <span className={k.fieldTop}>
        <label htmlFor={`${id}-d`} className={k.fieldLabel}>
          {props.label}
        </label>
        {props.requirement && (
          <span
            className={[
              k.fieldReq,
              props.requirement.kind === 'required' ? k.fieldReqRequired : k.fieldReqOptional,
            ].join(' ')}
          >
            {props.requirement.word}
          </span>
        )}
      </span>
      <span style={{ display: 'flex', gap: 8 }}>
        <select
          id={`${id}-d`}
          style={selectStyle}
          value={draft.day}
          onChange={(e) => update({ ...draft, day: e.currentTarget.value })}
          aria-label={`${props.label} — day`}
        >
          <option value="" />
          {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
            <option key={d} value={String(d)}>
              {numFmt.format(d)}
            </option>
          ))}
        </select>
        <select
          style={{ ...selectStyle, flex: 1.6 }}
          value={draft.month}
          onChange={(e) => update({ ...draft, month: e.currentTarget.value })}
          aria-label={`${props.label} — month`}
        >
          <option value="" />
          {months.map((m, i) => (
            <option key={m} value={String(i + 1)}>
              {m}
            </option>
          ))}
        </select>
        <select
          style={selectStyle}
          value={draft.year}
          onChange={(e) => update({ ...draft, year: e.currentTarget.value })}
          aria-label={`${props.label} — year`}
        >
          <option value="" />
          {years.map((y) => (
            <option key={y} value={String(y)}>
              {numFmt.format(y)}
            </option>
          ))}
        </select>
      </span>
      <span className={k.fieldHelp}>{props.help ?? ' '}</span>
    </div>
  );
}
